// src/pages/ProjectsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { Link, useNavigate } from "react-router-dom";
import { projectDisplayLabelPretty } from "../utils/format";
import { apiRequest } from "../api/http";
import { useTranslation } from "react-i18next";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

type StatusFilter = "All" | "Active" | "Settled";

// ---- API types (D1 schema) ----
type ApiProject = {
  id: string;
  tenant_id: string;
  client_id: string;
  name: string;
  address: string;

  // D1 rollup (kept updated by scope API)
  total_estimate?: number;

  status: string | null;
  archived: number; // 0/1
  created_at?: string;
  updated_at?: string;
};

type ApiPayment = {
  id: string;
  tenant_id: string;
  project_id: string;
  amount: number;
  payment_method: "Cash" | "Bank";
  date: string;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

export default function ProjectsPage() {
  const { t } = useTranslation();
  const {
    tenantId,
    clients,

    // legacy local stores (some may still be mock while migrating)
    scopeItems,
    paymentsReceived,
    expenses,
    subContracts,
    wageEntries,
    partnerDisbursements,

    deleteProjectAndRelated,
  } = useData() as any;

  const navigate = useNavigate();

  /* ======================
     API-backed Projects + Payments
  ====================== */
  const [projectsApi, setProjectsApi] = useState<ApiProject[]>([]);
  const [paymentsApi, setPaymentsApi] = useState<ApiPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reloadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest<ApiOk<ApiProject[]> | ApiErr>("GET", "/api/projects", { tenantId });
      if ((res as any).ok) {
        setProjectsApi(((res as any).data || []) as ApiProject[]);
      } else {
        setError((res as any).error || "Failed to load projects");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const reloadPayments = async () => {
    // Keep this non-blocking (projects still render even if payments fail)
    try {
      const res = await apiRequest<ApiOk<ApiPayment[]> | ApiErr>(
        "GET",
        "/api/payments_received",
        { tenantId }
      );
      if ((res as any).ok) {
        setPaymentsApi(((res as any).data || []) as ApiPayment[]);
      }
    } catch {
      // ignore for now
    }
  };

  useEffect(() => {
    reloadProjects();
    reloadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     UI state
  ====================== */
  const [clientId, setClientId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [address, setAddress] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [search, setSearch] = useState("");
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editClientId, setEditClientId] = useState("");
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  const resetAddForm = () => {
    setClientId("");
    setProjectName("");
    setAddress("");
    setFormError(null);
  };

  /* ======================
     Payments totals map (API)
  ====================== */
  const paymentsTotalByProjectId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of paymentsApi || []) {
      const pid = row.project_id;
      map[pid] = (map[pid] || 0) + (Number(row.amount) || 0);
    }
    return map;
  }, [paymentsApi]);

  const paymentsCountByProjectId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of paymentsApi || []) {
      const pid = row.project_id;
      map[pid] = (map[pid] || 0) + 1;
    }
    return map;
  }, [paymentsApi]);

  /* ======================
     Derived rows for UI
  ====================== */
  type ProjectRow = {
    id: string;
    clientId: string;
    name: string;
    address: string;
    status: string;
    archived: boolean;
    totalEstimate: number; // from D1 projects.total_estimate
    received: number;      // from D1 payments_received sums
  };

  const projects: ProjectRow[] = useMemo(() => {
    return projectsApi.map((p) => {
      const totalEstimate = Number((p as any).total_estimate ?? 0) || 0;
      const received = Number(paymentsTotalByProjectId[p.id] || 0);

      return {
        id: p.id,
        clientId: p.client_id,
        name: p.name,
        address: p.address,
        status: (p.status || "Active") as string,
        archived: Number(p.archived) === 1,
        totalEstimate,
        received,
      };
    });
  }, [projectsApi, paymentsTotalByProjectId]);

  /* ======================
     Actions (API)
  ====================== */
  const handleAdd = async () => {
    const n = projectName.trim();
    const a = address.trim();

    if (!clientId || !n || !a) {
      setFormError("Client, Project Name, and Address are required.");
      return;
    }

    const newId = generateId("P");

    try {
      const body = {
        id: newId,
        client_id: clientId,
        name: n,
        address: a,
        status: "Active",
        archived: 0,
      };

      const res = await apiRequest<ApiOk<ApiProject> | ApiErr>("POST", "/api/projects", { tenantId, body });

      if (!(res as any).ok) {
        setFormError((res as any).error || "Failed to create project");
        return;
      }

      const created = (res as any).data as ApiProject;
      setProjectsApi((prev) => [created, ...prev]);
      resetAddForm();

      navigate(`/projects/${created.id}?tab=scope`);
    } catch (e: any) {
      setFormError(e?.message || "Failed to create project");
    }
  };

  const startEdit = (p: ProjectRow) => {
    setEditingId(p.id);
    setEditClientId(p.clientId);
    setEditName(p.name);
    setEditAddress(p.address);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditClientId("");
    setEditName("");
    setEditAddress("");
  };

  const saveEdit = async (p: ProjectRow) => {
    if (!editClientId || !editName.trim() || !editAddress.trim()) {
      alert(t("projects.requiredFields"));
      return;
    }

    try {
      const patch = {
        client_id: editClientId,
        name: editName.trim(),
        address: editAddress.trim(),
      };

      const res = await apiRequest<ApiOk<ApiProject> | ApiErr>("PATCH", `/api/projects/${p.id}`, {
        tenantId,
        body: patch,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update project");
        return;
      }

      const updated = (res as any).data as ApiProject;
      setProjectsApi((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      cancelEdit();
    } catch (e: any) {
      alert(e?.message || "Failed to update project");
    }
  };

  const doArchive = async (p: ProjectRow) => {
    const ok = window.confirm(t("projects.archiveConfirm", { name: p.name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<ApiProject> | ApiErr>("PATCH", `/api/projects/${p.id}`, {
        tenantId,
        body: { archived: 1 },
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to archive project");
        return;
      }

      const updated = (res as any).data as ApiProject;
      setProjectsApi((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      if (editingId === p.id) cancelEdit();
    } catch (e: any) {
      alert(e?.message || "Failed to archive project");
    }
  };

  const doRestore = async (p: ProjectRow) => {
    const ok = window.confirm(t("projects.restoreConfirm", { name: p.name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<ApiProject> | ApiErr>("PATCH", `/api/projects/${p.id}`, {
        tenantId,
        body: { archived: 0 },
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to restore project");
        return;
      }

      const updated = (res as any).data as ApiProject;
      setProjectsApi((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      if (editingId === p.id) cancelEdit();
    } catch (e: any) {
      alert(e?.message || "Failed to restore project");
    }
  };

  // Hard delete (ONLY when empty)
  // NOTE: While migrating modules, be conservative: if estimate/received exist, we block delete.
  const doDeleteProjectIfEmpty = async (p: ProjectRow) => {
    // Conservative checks:
    const apiPaymentsCount = Number(paymentsCountByProjectId[p.id] || 0);
    const hasEstimateOrReceived = (Number(p.totalEstimate) || 0) !== 0 || (Number(p.received) || 0) !== 0;

    // Legacy local counts (may be stale during migration; we keep them as extra safety)
    const scopeCountLocal = (scopeItems || []).filter((s: any) => s.projectId === p.id).length;
    const paymentsCountLocal = (paymentsReceived || []).filter((x: any) => x.projectId === p.id).length;
    const expensesCount = (expenses || []).filter((x: any) => x.projectId === p.id).length;
    const subsCount = (subContracts || []).filter((x: any) => x.projectId === p.id).length;
    const wagesCount = (wageEntries || []).filter((x: any) => x.projectId === p.id).length;
    const disbCount = (partnerDisbursements || []).filter((x: any) => x.projectId === p.id).length;

    const isEmpty =
      !hasEstimateOrReceived &&
      apiPaymentsCount === 0 &&
      scopeCountLocal === 0 &&
      paymentsCountLocal === 0 &&
      expensesCount === 0 &&
      subsCount === 0 &&
      wagesCount === 0 &&
      disbCount === 0;

    if (!isEmpty) {
      alert(t("projects.deleteBlockedAlert"));
      return;
    }

    const ok = window.confirm(t("projects.deleteConfirm", { name: p.name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>("DELETE", `/api/projects/${p.id}`, { tenantId });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete project");
        return;
      }

      setProjectsApi((prev) => prev.filter((x) => x.id !== p.id));

      try {
        deleteProjectAndRelated?.(p.id);
      } catch {
        /* ignore */
      }

      if (editingId === p.id) cancelEdit();
    } catch (e: any) {
      alert(e?.message || "Failed to delete project");
    }
  };

  /* ======================
     Insights
  ====================== */
  const insightByProjectId = useMemo(() => {
    const map: Record<
      string,
      {
        cashSurplus: number;
        bankSurplus: number;
        toCollect: number;
        outstandingSubs: number;
        unpaidWages: number;
        needsAttention: boolean;
        readyToClose: boolean;
      }
    > = {};

    for (const p of projects) {
      const cashSurplus = 0;
      const bankSurplus = 0;

      const toCollect = Number(p.totalEstimate || 0) - Number(p.received || 0);

      const outstandingSubs = (subContracts || [])
        .filter((sc: any) => sc.projectId === p.id)
        .reduce((s: number, sc: any) => s + (Number(sc.balance) || 0), 0);

      const unpaidWages = (wageEntries || [])
        .filter((w: any) => w.projectId === p.id && !w.paid)
        .reduce((s: number, w: any) => s + (Number(w.totalWages) || 0), 0);

      const hasNegativePool = cashSurplus < 0 || bankSurplus < 0;
      const hasUnpaid = outstandingSubs > 0 || unpaidWages > 0;

      const needsAttention = hasNegativePool || hasUnpaid;
      const readyToClose = !hasUnpaid;

      map[p.id] = {
        cashSurplus,
        bankSurplus,
        toCollect,
        outstandingSubs,
        unpaidWages,
        needsAttention,
        readyToClose,
      };
    }

    return map;
  }, [projects, subContracts, wageEntries]);

  const visibleProjects = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filtered = projects.filter((p) => {
      if (!showArchived && p.archived) return false;

      if (statusFilter !== "All") {
        if (statusFilter === "Active" && p.status === "Settled") return false;
        if (statusFilter === "Settled" && p.status !== "Settled") return false;
      }

      if (q) {
        const clientName = clients.find((c: any) => c.id === p.clientId)?.name?.toLowerCase() || "";
        const hay = `${p.id} ${p.name} ${p.address} ${clientName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (onlyNeedsAttention) {
        if (!insightByProjectId[p.id]?.needsAttention) return false;
      }

      return true;
    });

    return filtered.slice().sort((a: any, b: any) => {
      const ia = insightByProjectId[a.id];
      const ib = insightByProjectId[b.id];

      const na = ia?.needsAttention ? 1 : 0;
      const nb = ib?.needsAttention ? 1 : 0;
      if (na !== nb) return nb - na;

      const sa = a.status === "Settled" ? 1 : 0;
      const sb = b.status === "Settled" ? 1 : 0;
      if (sa !== sb) return sa - sb;

      return (Number(b.received) || 0) - (Number(a.received) || 0);
    });
  }, [projects, clients, showArchived, statusFilter, search, onlyNeedsAttention, insightByProjectId]);

  const projectListHeaderRight = (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "end",
        justifyContent: "flex-end",
      }}
    >
      <div style={{ minWidth: 180 }}>
        <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>{t("common.status")}</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={{ width: "100%" }}
        >
          <option value="All">{t("common.all")}</option>
          <option value="Active">{t("common.active")}</option>
          <option value="Settled">{t("common.settled")}</option>
        </select>
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--pageText)",
          whiteSpace: "nowrap",
        }}
      >
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        {t("projects.showArchived")}
      </label>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--pageText)",
          whiteSpace: "nowrap",
        }}
      >
        <input
          type="checkbox"
          checked={onlyNeedsAttention}
          onChange={(e) => setOnlyNeedsAttention(e.target.checked)}
        />
        {t("projects.needsAttentionOnly")}
      </label>

      <button
        className="btn btnSecondary btnSm"
        type="button"
        onClick={() => {
          reloadProjects();
          reloadPayments();
        }}
        disabled={loading}
        title={t("projects.reloadBtn")}
      >
        {loading ? t("projects.refreshingBtn") : t("projects.refreshBtn")}
      </button>
    </div>
  );

  return (
    <div className="container">
      <style>{`
        .projFormGrid{
          display:grid;
          grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.6fr);
          gap:14px;
          align-items:start;
        }
        .projFormGrid .field{ min-width:0; }
        .projFormGrid .input, .projFormGrid select{ width:100%; box-sizing:border-box; }
        .projFormGrid .fullRow{ grid-column: 1 / -1; }
        @media (max-width: 980px){
          .projFormGrid{ grid-template-columns: 1fr; }
          .projFormGrid .fullRow{ grid-column:auto; }
        }

        .actionIcons{
          display:flex;
          gap:10px;
          align-items:center;
          justify-content:flex-start;
          flex-wrap:wrap;
        }
        .iconActionBtn{
          height: 36px;
          width: 36px;
          border-radius: 999px;
          display:grid;
          place-items:center;
          cursor:pointer;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          box-shadow: var(--shadow-sm);
          transition: transform 70ms ease, background 120ms ease, border-color 120ms ease, opacity 120ms ease;
        }
        .iconActionBtn:hover{ background: var(--table-row-hover); transform: translateY(-1px); }
        .iconActionBtn:active{ transform: translateY(0px); }

        .iconActionBtn.primary{
          border-color: rgba(79,107,237,0.28);
          color: var(--primary);
        }
        .iconActionBtn.danger{
          border-color: rgba(229,83,61,0.28);
          color: var(--bad);
        }
        .iconActionBtn.warn{
          border-color: rgba(240,167,43,0.34);
          color: #8a5200;
        }

        .iconGlyph{
          font-size: 14px;
          line-height: 1;
          font-weight: 900;
        }

        .actionsCell{
          white-space: nowrap;
        }
      `}</style>

      {/* Page header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>{t("projects.pageTitle")}</h1>
          <div className="pageNote">{t("projects.pageNote")}</div>
          {error ? <div style={{ marginTop: 8, color: "var(--bad)", fontWeight: 700 }}>{error}</div> : null}
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>{t("projects.searchLabel")}</label>
          <input
            className="input"
            placeholder={t("projects.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* ADD PROJECT */}
      <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("projects.addTitle")}</h3>
          <div className="cardSub">
            <span className="reqStar">*</span> {t("common.required")}
          </div>
        </div>

        <div className="projFormGrid">
          <div className="field">
            <label className="label">
              {t("projects.clientLabel")} <span className="reqStar">*</span>
            </label>
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                if (formError) setFormError(null);
              }}
            >
              <option value="">{t("projects.selectClient")}</option>
              {clients.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label">
              {t("projects.projectNameLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              placeholder={t("projects.projectNamePlaceholder")}
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                if (formError) setFormError(null);
              }}
            />
            <div className="pageNote" style={{ marginTop: 6 }}>
              {t("projects.projectNameTip")}
            </div>
          </div>

          <div className="field fullRow">
            <label className="label">
              {t("projects.projectAddressLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              placeholder={t("projects.projectAddressPlaceholder")}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                if (formError) setFormError(null);
              }}
            />
          </div>
        </div>

        {formError && (
          <div className="formError" role="alert">
            {formError}
          </div>
        )}

        <div className="formActions">
          <button className="btn" onClick={handleAdd} type="button">
            {t("projects.createProjectBtn")}
          </button>
          <button className="btn btnSecondary" onClick={resetAddForm} type="button">
            {t("projects.clearBtn")}
          </button>
        </div>
      </div>

      {/* PROJECT TABLE */}
      <div className="card">
        <div className="cardHeader">
          <div>
            <h3 className="cardTitle">{t("projects.listTitle")}</h3>
            <div className="cardSub">{loading ? "Loading..." : `${visibleProjects.length} projects`}</div>
          </div>

          {projectListHeaderRight}
        </div>

        <div className="tableWrap" style={{ maxHeight: "60vh", overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>{t("projects.colProject")}</th>
                <th>{t("projects.colEstimate")}</th>
                <th>{t("projects.colReceived")}</th>
                <th>{t("projects.colToCollect")}</th>
                <th>{t("projects.colCashSurplus")}</th>
                <th>{t("projects.colBankSurplus")}</th>
                <th>{t("projects.colReady")}</th>
                <th>{t("projects.colStatus")}</th>
                <th style={{ width: 220, textAlign: "right" }}>{t("projects.colActions")}</th>
              </tr>
            </thead>

            <tbody>
              {visibleProjects.map((p: any) => {
                const insights = insightByProjectId[p.id];
                const needsAttention = !!insights?.needsAttention;

                const cashSurplus = insights?.cashSurplus ?? 0;
                const bankSurplus = insights?.bankSurplus ?? 0;

                const isEditing = editingId === p.id;
                const projectLabel = projectDisplayLabelPretty(p, clients);

                // Delete eligibility (conservative during migration)
                const apiPaymentsCount = Number(paymentsCountByProjectId[p.id] || 0);
                const hasEstimateOrReceived =
                  (Number(p.totalEstimate) || 0) !== 0 || (Number(p.received) || 0) !== 0;

                const scopeCountLocal = (scopeItems || []).filter((s: any) => s.projectId === p.id).length;
                const paymentsCountLocal = (paymentsReceived || []).filter((x: any) => x.projectId === p.id).length;
                const expensesCount = (expenses || []).filter((x: any) => x.projectId === p.id).length;
                const subsCount = (subContracts || []).filter((x: any) => x.projectId === p.id).length;
                const wagesCount = (wageEntries || []).filter((x: any) => x.projectId === p.id).length;
                const disbCount = (partnerDisbursements || []).filter((x: any) => x.projectId === p.id).length;

                const canDelete =
                  !hasEstimateOrReceived &&
                  apiPaymentsCount === 0 &&
                  scopeCountLocal === 0 &&
                  paymentsCountLocal === 0 &&
                  expensesCount === 0 &&
                  subsCount === 0 &&
                  wagesCount === 0 &&
                  disbCount === 0;

                return (
                  <tr
                    key={p.id}
                    style={
                      p.archived
                        ? { opacity: 0.65 }
                        : needsAttention
                        ? { background: "rgba(217,119,6,0.08)" }
                        : undefined
                    }
                  >
                    <td>
                      {isEditing ? (
                        <div style={{ display: "grid", gap: 8 }}>
                          <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                          <input
                            className="input"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                          />
                          <select
                            value={editClientId}
                            onChange={(e) => setEditClientId(e.target.value)}
                            style={{ width: "100%" }}
                          >
                            <option value="">{t("projects.selectClient")}</option>
                            {clients.map((c: any) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <Link to={`/projects/${p.id}`} className="projectLink">
                              {projectLabel}
                            </Link>

                            {needsAttention ? <span className="badge badgeWarn">{t("projects.needsAttention")}</span> : null}
                            {p.archived ? <span className="badge">{t("projects.archived")}</span> : null}
                          </div>

                          {needsAttention ? (
                            <div style={{ fontSize: 12, color: "var(--warn)", marginTop: 6, fontWeight: 700 }}>
                              {(insights?.outstandingSubs || 0) > 0
                                ? `${t("projects.subBalancesLabel")}: ${money(insights?.outstandingSubs || 0)}`
                                : null}
                              {(insights?.outstandingSubs || 0) > 0 && (insights?.unpaidWages || 0) > 0 ? " • " : null}
                              {(insights?.unpaidWages || 0) > 0
                                ? `${t("projects.unpaidWagesLabel")}: ${money(insights?.unpaidWages || 0)}`
                                : null}
                            </div>
                          ) : null}
                        </>
                      )}
                    </td>

                    <td>{money(p.totalEstimate)}</td>
                    <td>{money(p.received)}</td>

                    <td className={(insights?.toCollect ?? 0) > 0 ? "numWarn" : "numGood"}>
                      {money(insights?.toCollect ?? 0)}
                    </td>

                    <td className={cashSurplus >= 0 ? "numGood" : "numBad"} title={t("projects.cashSurplusTip")}>
                      {money(cashSurplus)}
                    </td>

                    <td className={bankSurplus >= 0 ? "numGood" : "numBad"} title={t("projects.bankSurplusTip")}>
                      {money(bankSurplus)}
                    </td>

                    <td
                      style={{
                        fontWeight: 900,
                        color: insights?.readyToClose ? "var(--good)" : "var(--warn)",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        insights?.readyToClose
                          ? "No unpaid wages or subcontract balances detected"
                          : "Has unpaid wages and/or subcontract balances"
                      }
                    >
                      {insights?.readyToClose ? t("projects.readyYes") : t("projects.readyNo")}
                    </td>

                    <td style={{ fontWeight: 900, color: p.status === "Settled" ? "var(--good)" : "var(--primary)" }}>
                      {p.status === "Settled" ? t("projects.statusSettled") : p.status === "Archived" ? t("projects.statusArchived") : t("projects.statusActive")}
                    </td>

                    <td className="actionsCell" style={{ textAlign: "right" }}>
                      {isEditing ? (
                        <div className="actionsRow" style={{ justifyContent: "flex-end" }}>
                          <button className="btn btnSm" onClick={() => saveEdit(p)} type="button">{t("common.save")}</button>
                          <button className="btn btnSecondary btnSm" onClick={cancelEdit} type="button">{t("common.cancel")}</button>
                        </div>
                      ) : (
                        <div className="actionIcons" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="iconActionBtn primary"
                            onClick={() => startEdit(p)}
                            type="button"
                            title={t("projects.editProject")} aria-label={t("projects.editProject")}
                          >
                            <span className="iconGlyph">✎</span>
                          </button>

                          {!p.archived ? (
                            <button
                              className="iconActionBtn danger"
                              onClick={() => doArchive(p)}
                              type="button"
                              title={t("projects.archiveProject")} aria-label={t("projects.archiveProject")}
                            >
                              <span className="iconGlyph">🗔</span>
                            </button>
                          ) : (
                            <button
                              className="iconActionBtn warn"
                              onClick={() => doRestore(p)}
                              type="button"
                              title={t("projects.restoreProject")} aria-label={t("projects.restoreProject")}
                            >
                              <span className="iconGlyph">↺</span>
                            </button>
                          )}

                          <button
                            className="iconActionBtn primary"
                            onClick={() => navigate(`/projects/${p.id}?tab=scope`)}
                            type="button"
                            title={t("projects.openScope")} aria-label={t("projects.openScope")}
                          >
                            <span className="iconGlyph">▦</span>
                          </button>

                          {canDelete ? (
                            <button
                              className="iconActionBtn danger"
                              onClick={() => doDeleteProjectIfEmpty(p)}
                              type="button"
                              title={t("projects.deleteProject")} aria-label={t("projects.deleteProject")}
                            >
                              <span className="iconGlyph">🗑</span>
                            </button>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!loading && visibleProjects.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                    {t("projects.noProjects")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("projects.deleteTip")}
        </div>
      </div>
    </div>
  );
}



